import React, { useCallback, useEffect, useState } from 'react';
import CoachReview from './CoachReview';
import { errorText, feelingEmoji, formatDate, formatKg, formatSet, parseApiDate, setsFor, stravaActivityUrl } from './strengthFormat';

const IN_FLIGHT = ['Pending', 'Uploading'];

function StravaBadge({ t, session, canRetry, onRetry }) {
  switch (session.stravaSyncStatus) {
    case 'Synced':
      return session.stravaActivityId ? (
        <a className="st-strava-link" href={stravaActivityUrl(session.stravaActivityId)} target="_blank" rel="noopener noreferrer">
          {t('strength.strava.viewOnStrava')}
        </a>
      ) : <span className="st-badge st-badge-strava">{t('strength.history.onStrava')}</span>;
    case 'Pending':
    case 'Uploading':
      return <span className="st-badge">🔄 {t('strength.history.uploading')}</span>;
    case 'Failed':
    case 'NotConnected':
      return (
        <span className="st-strava-problem">
          <span className="st-badge st-badge-warn" title={session.stravaSyncError || ''}>
            {session.stravaSyncStatus === 'Failed' ? t('strength.history.failed') : t('strength.history.notSent')}
          </span>
          {canRetry && <button type="button" className="st-link-btn" onClick={onRetry}>{t('strength.history.retry')}</button>}
        </span>
      );
    default:
      return null;
  }
}

function SessionHistory({ api, t, lang, members, showMessage, stravaStatus, onStravaChanged }) {
  const [userId, setUserId] = useState('');
  const [sessions, setSessions] = useState(null);
  const [open, setOpen] = useState({});
  const [reviewing, setReviewing] = useState({});

  const askCoach = async (session) => {
    setReviewing(prev => ({ ...prev, [session.id]: true }));
    try {
      const reviewed = await api.review(session.id);
      setSessions(prev => prev.map(s => (s.id === session.id ? { ...s, ...reviewed } : s)));
    } catch (e) {
      showMessage(errorText(e, t('strength.review.failed')), 'error');
    } finally {
      setReviewing(prev => ({ ...prev, [session.id]: false }));
    }
  };

  const load = useCallback(async () => {
    try {
      setSessions(await api.sessions(userId || undefined) || []);
    } catch (e) {
      setSessions([]);
      showMessage(errorText(e, t('strength.errors.load')), 'error');
    }
  }, [api, userId, showMessage, t]);

  useEffect(() => { load(); }, [load]);

  // Uploads happen in the background; refresh while any of mine are on their way.
  const uploading = (sessions || []).some(s => s.isMine && IN_FLIGHT.includes(s.stravaSyncStatus));
  useEffect(() => {
    if (!uploading) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [uploading, load]);

  const retry = async (session) => {
    try {
      const updated = await api.retryStrava(session.id);
      setSessions(prev => prev.map(s => (s.id === session.id ? { ...s, ...updated, sets: s.sets } : s)));
      onStravaChanged();
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    }
  };

  const remove = async (session) => {
    const key = session.stravaSyncStatus === 'Synced' ? 'strength.history.deleteConfirmStrava' : 'strength.history.deleteConfirm';
    if (!window.confirm(t(key))) return;
    try {
      await api.deleteSession(session.id);
      setSessions(prev => prev.filter(s => s.id !== session.id));
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    }
  };

  return (
    <div className="st-history">
      {members.length > 1 && (
        <div className="st-chips" role="group" aria-label={t('strength.history.whose')}>
          <button type="button" className={`st-chip ${userId === '' ? 'active' : ''}`} onClick={() => setUserId('')}>
            {t('strength.common.everyone')}
          </button>
          {members.map(m => (
            <button type="button" key={m.userId} className={`st-chip ${userId === m.userId ? 'active' : ''}`} onClick={() => setUserId(m.userId)}>
              {m.name}
            </button>
          ))}
        </div>
      )}

      {sessions === null && <div className="st-empty">{t('strength.loading')}</div>}
      {sessions?.length === 0 && <div className="st-empty">{t('strength.history.empty')}</div>}

      <ul className="st-session-list">
        {(sessions || []).map(session => {
          const exerciseKeys = [...new Set(session.sets.map(s => s.exerciseKey))];
          return (
            <li key={session.id} className="st-card st-session">
              <button
                type="button"
                className="st-session-head"
                aria-expanded={!!open[session.id]}
                onClick={() => setOpen(prev => ({ ...prev, [session.id]: !prev[session.id] }))}
              >
                <span className="st-session-date">{formatDate(parseApiDate(session.startedAt), lang)}</span>
                <span className="st-session-name">
                  {session.name}
                  {session.feeling ? <span aria-label={t(`strength.feelings.${session.feeling}`)}> {feelingEmoji(session.feeling)}</span> : null}
                  {!session.isMine && <span className="st-badge">{session.ownerName}</span>}
                </span>
                <span className="st-muted st-session-meta">
                  {t('strength.summary.minutes', { count: session.durationMinutes ?? 0 })}
                  {' · '}{t('strength.logger.setsDone', { count: session.workingSets })}
                  {session.volumeKg > 0 && <> · {formatKg(Math.round(session.volumeKg), lang)}</>}
                </span>
              </button>
              <div className="st-session-actions">
                {session.isMine && (
                  <StravaBadge
                    t={t}
                    session={session}
                    canRetry={!!stravaStatus?.connected}
                    onRetry={() => retry(session)}
                  />
                )}
                {session.isMine && (
                  <button type="button" className="st-link-btn st-danger-text" onClick={() => remove(session)}>
                    {t('strength.common.delete')}
                  </button>
                )}
              </div>

              {open[session.id] && (
                <div className="st-session-detail">
                  {exerciseKeys.map(key => {
                    const sets = setsFor(session.sets, key);
                    const note = (session.exerciseNotes || []).find(n => n.exerciseKey === key)?.note;
                    return (
                      <div key={key} className="st-session-exercise">
                        <strong>{sets[0].exerciseName}</strong>
                        <span className="st-muted">
                          {sets.map(s => `${s.isWarmup ? 'W ' : ''}${formatSet(s, lang)}`).join(', ')}
                        </span>
                        {note && <span className="st-last-note">📝 «{note}»</span>}
                      </div>
                    );
                  })}
                  {session.notes && <p className="st-cue">📝 {session.notes}</p>}
                  <CoachReview t={t} session={session} loading={!!reviewing[session.id]} />
                  {session.isMine && !session.coachPraise && !reviewing[session.id] && (
                    <button type="button" className="st-btn st-btn-small st-btn-ai" onClick={() => askCoach(session)}>
                      🧭 {t('strength.review.ask')}
                    </button>
                  )}
                  {session.isMine && session.stravaSyncError && <p className="st-error">{session.stravaSyncError}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default SessionHistory;
